import { OC_CENTER, seatKey, type OcSens } from './oc-learn';

/**
 * A crime's chance of success (owner, 2026-10-22: "This should be derivable based on the scenario and data provided by
 * torn probability + our members cpr in each slot"). tornprobability.com computes it from a crime's scenario and the pass
 * rate in each seat — its own model, which it doesn't publish (only role weights, GetRoleWeights), so Omni-Torn can't
 * work it out without asking: it sends the scenario's name and the seats' pass rates (numbers only, no names, no ids) and
 * shows the answer labelled as tornprobability's calculation. Pure: the request, its cache key and the answer.
 * The service that asks (cache, rate limit, the player's switch and terms) is the extension's bg/oc-success.ts.
 *
 * The API (as the public "OC2.0 Helper" userscript uses it, greasyfork 577968):
 *  - GET  /api/GetRoleNames → `{ "Break the Bank": { "P1": "Robber", "P2": "Muscle 1", … }, … }` (seen live 2026-10-22) — each crime's seats in
 *    the order its model takes them (sorted by the number in each key);
 *  - POST /api/CalculateSuccess `{ scenario: "Break the Bank", parameters: [pass rates in that order] }` →
 *    `{ successChance: 0…1 }`.
 * Where it has no answer, Omni-Torn's own model from finished crimes (oc-learn.ts) may give an estimate — said as such,
 * with how many crimes it learned from, and only from OC_ESTIMATE_MIN_CRIMES up.
 */

export const TP_BASE = 'https://tornprobability.com:3000/api';
export const TP_ROLE_NAMES = `${TP_BASE}/GetRoleNames`;
export const TP_CALCULATE = `${TP_BASE}/CalculateSuccess`;

/** tornprobability's own name matching: lower case, letters and digits only ("Muscle #1" → "muscle1"). */
export const tpKey = (s: string): string =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // "Guardian Ángels" (tornprobability's spelling) = "Guardian Angels"
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** GetRoleNames' answer → each crime's seat names in the model's order, by tpKey of the crime. */
export function readRoleNames(body: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!body || typeof body !== 'object' || Array.isArray(body)) return out;
  for (const [crime, roles] of Object.entries(body as Record<string, unknown>)) {
    if (!roles || typeof roles !== 'object' || Array.isArray(roles)) continue;
    const list = Object.entries(roles as Record<string, unknown>)
      .filter(([, v]) => typeof v === 'string' && v.trim())
      .sort(([a], [b]) => (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0))
      .map(([, v]) => (v as string).trim().slice(0, 60));
    if (list.length) out[tpKey(crime)] = list.slice(0, 12);
  }
  return out;
}

/** One seat of a crime: its name as Torn writes it and the pass rate in it (null: open / not known). */
export interface SuccessSeat {
  role: string;
  rate: number | null;
}

export interface SuccessRequest {
  scenario: string;
  parameters: number[];
}

/**
 * The CalculateSuccess body for a crime — only when tornprobability knows the crime's seats and every one of them has a
 * pass rate (an open seat has none: there's nothing to calculate yet). Rates are whole numbers 0–100.
 */
export function successRequest(names: Record<string, string[]> | null | undefined, scenario: string, seats: readonly SuccessSeat[]): SuccessRequest | null {
  const order = names?.[tpKey(scenario)];
  if (!order?.length || !scenario.trim()) return null;
  const by = new Map<string, number | null>();
  for (const s of seats) by.set(tpKey(s.role), s.rate);
  if (by.size !== order.length) return null;
  const parameters: number[] = [];
  for (const role of order) {
    const r = by.get(tpKey(role));
    if (typeof r !== 'number' || !Number.isFinite(r) || r < 0 || r > 100) return null;
    parameters.push(Math.round(r));
  }
  return { scenario: scenario.trim().slice(0, 60), parameters };
}

/** The cache key: the same crime with the same pass rates asks once. */
export const successKey = (r: SuccessRequest): string => `${tpKey(r.scenario)}|${r.parameters.join(',')}`;

/** CalculateSuccess' answer → 0…1, or null (never a made-up number). */
export function readSuccess(body: unknown): number | null {
  const v = body && typeof body === 'object' ? (body as { successChance?: unknown }).successChance : null;
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1 ? v : null;
}

/** Omni-Torn's own estimate is shown only from this many finished crimes of that scenario. */
export const OC_ESTIMATE_MIN_CRIMES = 50;

/**
 * Omni-Torn's estimate from its learned model (oc-learn.ts: P = σ(bias + Σ coef × (rate − 70) / 10), the coefficients
 * being the learned weights' shares of the model's scale) — only with every seat's rate and enough finished crimes.
 */
export function learnedEstimate(weights: Record<string, number> | null | undefined, sens: OcSens | null | undefined, seats: readonly SuccessSeat[]): { chance: number; n: number } | null {
  if (!weights || !sens || sens.n < OC_ESTIMATE_MIN_CRIMES || !(sens.scale > 0) || !seats.length) return null;
  const total = Object.values(weights).reduce((a, b) => a + (Number.isFinite(b) && b > 0 ? b : 0), 0);
  if (total <= 0) return null;
  let z = sens.bias;
  for (const s of seats) {
    if (typeof s.rate !== 'number' || !Number.isFinite(s.rate)) return null;
    const w = weights[seatKey(s.role)] ?? weights[seatKey(s.role.replace(/\s*#\d+$/, ''))];
    if (typeof w !== 'number') return null;
    z += ((sens.scale * w) / total) * ((s.rate - OC_CENTER) / 10);
  }
  return { chance: 1 / (1 + Math.exp(-z)), n: sens.n };
}

/** "61.7%" from 0.617. */
export const chanceText = (c: number): string => `${(Math.round(c * 1000) / 10).toFixed(1)}%`;
